import React, { useState, useEffect } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import api from "../utils/api";
import { CheckCircle2, XCircle, Clock, User, Loader2, RefreshCw, ChevronRight, Image, MapPin, Link2 } from "lucide-react";

const SOCIAL_LABELS = {
  instagram: "Instagram", facebook: "Facebook", soundcloud: "SoundCloud",
  beatport: "Beatport", spotify: "Spotify", youtube: "YouTube",
  tiktok: "TikTok", twitch: "Twitch",
};

export default function AdminBioApprovalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bio-approvals/");
      setItems(res.data);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id) => {
    setActionLoading(id + "_approve");
    try {
      await api.post(`/admin/bio-approvals/${id}/approve`);
      setItems(prev => prev.filter(i => i.id !== id));
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    setActionLoading(id + "_reject");
    try {
      await api.post(`/admin/bio-approvals/${id}/reject`);
      setItems(prev => prev.filter(i => i.id !== id));
    } finally { setActionLoading(null); }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Artist-Änderungen</h1>
            <p className="text-zinc-500 text-sm mt-0.5">Bio, Profilbild, Social-Links und weitere Felder die auf Freigabe warten</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 text-sm text-zinc-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-zinc-400" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16">
            <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-zinc-500">Keine ausstehenden Änderungen – alles erledigt!</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden divide-y divide-white/5">
            {items.map(item => {
              const pc = item.pending_changes || {};
              const hasBio = !!item.pending_bio;
              const changeCount = (hasBio ? 1 : 0) + Object.keys(pc).length;
              return (
                <div key={item.id}>
                  {/* Header Row */}
                  <button
                    onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/[0.02] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                      {item.bild_url
                        ? <img src={item.bild_url} alt={item.name} className="w-full h-full object-cover" />
                        : <User size={18} className="text-zinc-500" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white">{item.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <Clock size={11} className="text-amber-500" />
                        <span className="text-xs text-amber-400 font-medium">
                          {changeCount} Änderung{changeCount !== 1 ? "en" : ""} ausstehend
                        </span>
                        {hasBio && <span className="text-xs bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-md font-medium">Bio</span>}
                        {pc.bild_url && <span className="text-xs bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded-md font-medium">Bild</span>}
                        {pc.bild_url && <span className="text-xs bg-sky-500/15 text-sky-400 px-1.5 py-0.5 rounded-md font-medium">Bild</span>}
                      </div>
                    </div>
                    <ChevronRight size={16} className={`text-zinc-500 transition-transform ${expanded === item.id ? "rotate-90" : ""}`} />
                  </button>

                  {/* Expanded */}
                  {expanded === item.id && (
                    <div className="border-t border-white/5 px-5 py-5 space-y-5 bg-zinc-950/40">

                      {/* Bio-Vergleich */}
                      {hasBio && (
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Biografie</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-zinc-500 mb-1.5">Aktuell (live)</p>
                              <div className="bg-zinc-800/60 rounded-lg p-3 text-sm text-zinc-400 leading-relaxed min-h-[60px]">
                                {item.current_bio || <span className="italic text-zinc-600">Keine Bio vorhanden</span>}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-300 font-semibold mb-1.5">Neu (ausstehend)</p>
                              <div className="bg-zinc-800 border-l-2 border-amber-500 rounded-lg p-3 text-sm text-zinc-100 leading-relaxed min-h-[60px]">
                                {item.pending_bio}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Profilbild */}
                      {pc.bild_url && (
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
                            <Image size={11} /> Profilbild
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-zinc-500 mb-1.5">Aktuell</p>
                              {item.bild_url
                                ? <img src={item.bild_url} alt="" className="w-20 h-20 rounded-xl object-cover ring-1 ring-white/10" />
                                : <p className="text-xs text-zinc-600 italic">Kein Bild</p>}
                            </div>
                            <div>
                              <p className="text-xs text-blue-400 mb-1.5">Neu</p>
                              <img src={pc.bild_url} alt="" className="w-20 h-20 rounded-xl object-cover ring-2 ring-blue-500/40" onError={e => { e.target.style.opacity = 0.3; }} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Profilbild */}
                      {pc.bild_url && (
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Profilbild</p>
                          <div className="grid grid-cols-2 gap-4">
                            {/* Aktuell */}
                            <div>
                              <p className="text-xs text-zinc-500 mb-2">Aktuell (live)</p>
                              <div className="flex items-center gap-3">
                                {item.current_bild_url ? (
                                  <img src={item.current_bild_url} alt="" className="w-12 h-12 rounded-full object-cover bg-zinc-700 flex-shrink-0" />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                                    <span className="text-zinc-600 text-xs">–</span>
                                  </div>
                                )}
                                <span className="text-zinc-500 text-xs truncate max-w-[160px]">{item.current_bild_url || "Kein Bild"}</span>
                              </div>
                            </div>
                            {/* Neu */}
                            <div>
                              <p className="text-xs text-zinc-300 font-semibold mb-2">Neu (ausstehend)</p>
                              <div className="flex items-center gap-3">
                                <img src={pc.bild_url} alt="" className="w-12 h-12 rounded-full object-cover bg-zinc-700 flex-shrink-0 ring-2 ring-amber-500/40" />
                                <span className="text-zinc-100 text-xs truncate max-w-[160px]">{pc.bild_url}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                        <button onClick={() => handleApprove(item.id)} disabled={!!actionLoading}
                          data-testid={`approve-bio-${item.id}`}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition-colors disabled:opacity-50">
                          {actionLoading === item.id + "_approve" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                          Alle genehmigen
                        </button>
                        <button onClick={() => handleReject(item.id)} disabled={!!actionLoading}
                          data-testid={`reject-bio-${item.id}`}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-bold transition-colors disabled:opacity-50">
                          {actionLoading === item.id + "_reject" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                          Alle ablehnen
                        </button>
                        {item.slug && (
                          <a href={`/artist/${item.slug}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors ml-auto">
                            Artist-Seite →
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
